import type {EventPromotionReducerState, PrintFlyerPoster, PromotionStep, PromotionSteps} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import {PromotionType} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import type {EventHashedID} from '@Components/events/events.types';
import {isEventPromotionPage, isManageEventPage} from '@Libraries/my-stuff/my-stuff-library';
import {LoadingStates} from '@Utils/loading.util';
import type {ManageEventPageReduxState} from '@Components/mystuff-manage-event-page/mystuff-manage-event-page.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {isPAYGUser} from '@Utils/user.util';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {getAjaxParamsForUpdatingEmailCampaignContent, getCampaignPublishWizardUri, isCampaignDeleted, isCampaignSuspended, isDraftCampaign, openCampaignInWizard} from '@Libraries/email-campaigns-library';
import {OneClickPromoItemState} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {isPostPublished} from '@Libraries/social-media';
import {EventUpsellType, openEventsUpsellDialog} from "@Libraries/events.library";
import {openOneClickPromotionModal} from "@Modals/event-one-click-promotion-modal/event-one-click-promotion-modal.library";

export const PROMOTION_MYSTUFF_CONTAINER_QUERY: ContainerQueryObject = {
  navXS: {
    maxWidth: 450,
  },

  small: {
    minWidth: 0,
    maxWidth: 600,
  },

  mediumAndLaptopSize: {
    minWidth: 601,
    maxWidth: 950,
  },

  xlarge: {
    minWidth: 951,
  },
};

export const EVENT_PROMOTION_LEARN_MORE_PAGE_URL = 'https://www.postermywall.com/index.php/m/event-marketing-tool/#how-can-i-promote-my-event';

export const isEventLoadedInPromotePage = (reduxState: EventPromotionReducerState, eventPageHash: string, eventHashedId: EventHashedID): boolean => {
  return isEventPromotionPage(eventPageHash) && reduxState.event.hashedID === eventHashedId && reduxState.loadingState === LoadingStates.LOADED;
};

export const isEventLoadedInManagePage = (reduxState: ManageEventPageReduxState, eventPageHash: string, eventHashedId: EventHashedID): boolean => {
  return isManageEventPage(eventPageHash) && reduxState.event.hashedID === eventHashedId && reduxState.loadingState === LoadingStates.LOADED;
};

export const isPromotionStepCompletedOrSkipped = (step: PromotionStep): boolean => {
  return step.isCompleted || step.isSkipped;
};

export const getCompletedOrSkippedPromotionSteps = (promotionSteps: PromotionSteps): PromotionSteps => {
  return promotionSteps.filter((step) => {
    return isPromotionStepCompletedOrSkipped(step);
  });
};

export const getCompletedPromotionSteps = (promotionSteps: PromotionSteps): PromotionSteps => {
  return promotionSteps.filter((step) => {
    return step.isCompleted;
  });
};

export const getSkippedPromotionSteps = (promotionSteps: PromotionSteps): PromotionSteps => {
  return promotionSteps.filter((step) => {
    return step.isSkipped;
  });
};

export const getIncompletePromotionSteps = (promotionSteps: PromotionSteps): PromotionSteps => {
  return promotionSteps.filter((step) => {
    return !isPromotionStepCompletedOrSkipped(step);
  });
};

export const getPromotionStepFromType = (promotionSteps: PromotionSteps, promotionType: PromotionType): PromotionStep | undefined => {
  return promotionSteps.find((step) => {
    return step.type === promotionType;
  });
};

export const sortPromotionSteps = (steps: PromotionSteps): PromotionSteps => {
  return steps.sort((stepA, stepB) => {
    return stepA.sortOrder - stepB.sortOrder;
  });
};

export const doesStepHavePremiumUpselling = (step: PromotionStep): boolean => {
  return step.type === PromotionType.EMAIL_CAMPAIGNS && isPAYGUser();
};

export const getDummyPrintFlyerItemFromHID = (hashedId: string): PrintFlyerPoster => {
  return {
    posterHashedID: hashedId,
    posterName: '',
    isVideo: false,
    idcreator: -1,
    posterId: -1,
    isPreviewOutdated: true,
    previewImageURL: '',
    previewVideoURL: '',
    isLoading: true,
  };
};

export const startOneClickPromotionFlowForEvent = (eventHashedId: EventHashedID): void => {
  if (isPAYGUser()) {
    openEventsUpsellDialog(EventUpsellType.EVENT_ONE_CLICK_PROMOTION);
    return;
  }

  openOneClickPromotionModal({
    eventHashedId,
  });
}

export const isOneClickEmailCampaignInErrorState = (campaign: EmailCampaign, campaignOneClickState: OneClickPromoItemState): boolean => {
  return campaignOneClickState === OneClickPromoItemState.ERROR || isCampaignDeleted(campaign) || isCampaignSuspended(campaign);
};

export const isOneClickEmailCampaignInSuccessState = (campaign: EmailCampaign, campaignOneClickState: OneClickPromoItemState): boolean => {
  return campaignOneClickState === OneClickPromoItemState.SUCCESS || (!isDraftCampaign(campaign) && !isOneClickEmailCampaignInErrorState(campaign, campaignOneClickState));
};

export const isOneClickSocialPostInErrorState = (socialPost: SocialPost, postOneClickState: OneClickPromoItemState): boolean => {
  return postOneClickState === OneClickPromoItemState.ERROR || (socialPost.state === PostState.ERROR || socialPost.state === PostState.SCHEDULE_ERROR);
};

export const isOneClickSocialPostInSuccessState = (socialPost: SocialPost, postOneClickState: OneClickPromoItemState): boolean => {
  return postOneClickState === OneClickPromoItemState.SUCCESS || isPostPublished(socialPost.state);
};

export const openEmailCampaignInWizardForEventPromotion = (campaign: EmailCampaign, eventHashedID: EventHashedID): void => {
  window.PMW.redirectUrlCache.setCache(
      window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_CACHE_KEY,
      window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_REDIRECT_URI + `/${eventHashedID}`,
      window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_DEFAULT_CACHE_TIMEOUT_MILLISECONDS
  );

  openCampaignInWizard(getCampaignPublishWizardUri(campaign));
}

export const doSaveOneClickSocialPostOnBackend = (post: SocialPost): Promise<void> => {
  const accountIds = post.accounts.map((account) => {
    return account.id;
  });

  return window.PMW.writeLocal('socialmedia/saveSocialPostDetails', {
    idSocialPost: post.id,
    caption: post.caption,
    accountIds: accountIds,
  });
};

export const doSaveOneClickCampaignOnBackend = (campaign: EmailCampaign): Promise<void> => {
  return window.PMW.writeLocal('emailmarketing/updateEmailCampaignContentAndMailingLists', getAjaxParamsForUpdatingEmailCampaignContent(campaign));
}